import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryTick: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary] caught:', {
      message: error?.message,
      stack: error?.stack?.slice(0, 400),
      componentStack: errorInfo?.componentStack?.slice(0, 400),
    });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryTick: this.state.retryTick + 1,
    });
  };

  handleDismiss = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const sanitizedMessage =
        this.state.error?.message && typeof this.state.error.message === 'string'
          ? this.state.error.message.replace(/[<>&"']/g, (c) => ({
              '<': '&lt;',
              '>': '&gt;',
              '&': '&amp;',
              '"': '&quot;',
              "'": '&#39;',
            }[c]))
          : 'Something went wrong while rendering this page.';

      return (
        <div className="w-full h-full min-h-[500px] flex items-center justify-center">
          <div
            className="relative max-w-[480px] w-[86%] rounded-2xl p-5 text-slate-100 shadow-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(127,29,29,0.85) 0%, rgba(15,23,42,0.92) 55%, rgba(30,41,59,0.9) 100%)',
              border: '1px solid rgba(250,204,21,0.28)',
              boxShadow: '0 25px 60px -15px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)',
            }}
          >
            <div
              className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(250,204,21,0.18), transparent 60%)' }}
            />
            <div className="relative flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
                  boxShadow: '0 8px 20px -8px rgba(127,29,29,0.8), inset 0 1px 0 rgba(255,255,255,0.18)',
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[15.5px] font-extrabold tracking-tight text-yellow-300 drop-shadow">
                  Couldn&apos;t load page content
                </h4>
                <p className="text-[11.5px] text-slate-300 mt-1 leading-relaxed break-words">
                  {sanitizedMessage}
                </p>
                <p className="text-[10.5px] text-slate-400 mt-1.5 italic">
                  Try reloading this module or dismiss to clear the error state.
                </p>
                <div className="flex items-center gap-2 mt-3.5 flex-wrap">
                  <button
                    onClick={this.handleRetry}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg,#eab308 0%,#fbbf24 50%,#fde047 100%)',
                      boxShadow: '0 8px 18px -8px rgba(120,53,15,0.7), inset 0 1px 0 rgba(255,255,255,0.35)',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                    </svg>
                    Retry
                  </button>
                  <button
                    onClick={this.handleDismiss}
                    className="px-3 py-2 rounded-xl text-[12px] font-semibold text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
